import { useEffect, useRef, useState } from 'react';
import {
  HIGHLIGHTER_COLORS, INK_COLORS, PEN_ORDER, PEN_PROFILES, profileOf,
  type PenKind, type Tool,
} from 'shared';
import type { EngineMode, EraserSettings, PenSettings, SelectSettings } from '../ink/types.js';

export interface ToolState {
  mode: EngineMode;
  /** Qual das duas pontas está em uso. Guardar isto separado é o que impede
   *  o marca-texto de "pintar" o botão da caneta com a cor dele. */
  active: 'pen' | 'highlighter';
  pen: PenSettings;
  highlighter: PenSettings;
  eraser: EraserSettings;
  select: SelectSettings['mode'];
}

const STORAGE_KEY = 'cv.tools';

export function loadToolState(): ToolState {
  const fallback: ToolState = {
    mode: 'draw',
    active: 'pen',
    pen: { kind: 'ballpoint', color: INK_COLORS[0] as string, size: PEN_PROFILES.ballpoint.defaultSize, opacity: 1 },
    highlighter: { kind: 'highlighter', color: HIGHLIGHTER_COLORS[0] as string, size: PEN_PROFILES.highlighter.defaultSize, opacity: PEN_PROFILES.highlighter.defaultOpacity },
    eraser: { mode: 'stroke', size: 18, highlighterOnly: false },
    select: 'lasso',
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const saved = JSON.parse(raw) as Partial<ToolState>;
    const pen = { ...fallback.pen, ...saved.pen };
    // Estado salvo por uma versão anterior podia gravar o marca-texto no lugar
    // da caneta. Sanear na leitura é mais barato do que pedir ao usuário para
    // limpar os dados do app.
    if (pen.kind === 'highlighter') Object.assign(pen, fallback.pen);
    const highlighter = { ...fallback.highlighter, ...saved.highlighter, kind: 'highlighter' as Tool };
    return {
      ...fallback,
      ...saved,
      pen,
      highlighter,
      eraser: { ...fallback.eraser, ...saved.eraser },
      // O modo nunca é restaurado: abrir a página na borracha seria uma
      // armadilha — o primeiro traço apagaria em vez de escrever.
      mode: 'draw',
    };
  } catch {
    return fallback;
  }
}

export function saveToolState(state: ToolState): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* modo anônimo */ }
}

const RECENT_KEY = 'cv.recentColors';

function recentColors(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[]; } catch { return []; }
}

function pushRecent(color: string): string[] {
  const list = [color, ...recentColors().filter((c) => c !== color)].slice(0, 8);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch { /* ignora */ }
  return list;
}

/** Amostra da ponta ativa: cor e espessura reais, como no Samsung Notes. */
function PenChip({ pen, active }: { pen: PenSettings; active: boolean }): JSX.Element {
  const d = Math.min(22, 6 + pen.size * 1.2);
  return (
    <span className="pen-plate plate" aria-hidden style={{ opacity: active ? 1 : 0.55 }}>
      <span
        className="pen-chip"
        style={{
          width: d, height: d, background: pen.color,
          borderRadius: profileOf(pen.kind).kind === 'highlighter' ? 3 : '50%',
        }}
      />
    </span>
  );
}

function ColorGrid({ colors, value, onPick }: {
  colors: string[]; value: string; onPick(c: string): void;
}): JSX.Element {
  return (
    <div className="color-grid">
      {colors.map((c) => (
        <button
          key={c}
          className="color-dot"
          aria-label={c}
          aria-pressed={value.toLowerCase() === c.toLowerCase()}
          style={{ background: c }}
          onClick={() => onPick(c)}
        />
      ))}
    </div>
  );
}

function SizeRow({ sizes, value, color, onPick }: {
  sizes: number[]; value: number; color: string; onPick(s: number): void;
}): JSX.Element {
  return (
    <div className="size-row">
      {sizes.map((s) => (
        <button
          key={s}
          className="size-dot"
          aria-label={`espessura ${s}`}
          aria-pressed={Math.abs(value - s) < 0.01}
          onClick={() => onPick(s)}
        >
          <span style={{ width: 4 + s * 1.6, height: 4 + s * 1.6, background: color }} />
        </button>
      ))}
    </div>
  );
}

interface Props {
  state: ToolState;
  onChange(next: ToolState): void;
  onEraseAll(): void;
  onKeyboard(): void;
  selectionCount: number;
  onDeleteSelection(): void;
  onRecolorSelection(color: string): void;
  onUndo(): void;
  onRedo(): void;
  onZoom(factor: number): void;
  onFit(): void;
  onMenu(): void;
  sendCount: number;
  sending: boolean;
  onSend(): void;
}

type Popover = null | 'pen' | 'highlighter' | 'eraser' | 'select' | 'selection';

export default function ToolBar(p: Props): JSX.Element {
  const [open, setOpen] = useState<Popover>(null);
  const [recent, setRecent] = useState<string[]>(recentColors());
  const [confirmErase, setConfirmErase] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(null);
    };
    // `capture` para fechar antes de o toque virar traço no canvas.
    document.addEventListener('pointerdown', close, true);
    return () => document.removeEventListener('pointerdown', close, true);
  }, [open]);

  useEffect(() => { setOpen(p.selectionCount ? 'selection' : null); }, [p.selectionCount]);
  useEffect(() => { if (open !== 'eraser') setConfirmErase(false); }, [open]);

  const s = p.state;
  const set = (patch: Partial<ToolState>): void => p.onChange({ ...s, ...patch });
  const isPen = s.mode === 'draw' && s.active === 'pen';
  const isHl = s.mode === 'draw' && s.active === 'highlighter';

  /** Tocar na ferramenta já ativa abre os ajustes dela. */
  const pick = (mode: EngineMode, popover: Popover, extra?: Partial<ToolState>): void => {
    const already = open === popover;
    if (extra) set({ mode, ...extra });
    else set({ mode });
    setOpen(already ? null : popover);
  };

  return (
    <div className="topbar toolbar" ref={ref}>
      <button aria-pressed={isPen} title={profileOf(s.pen.kind).label}
        onClick={() => pick('draw', 'pen', { active: 'pen' })}>
        <PenChip pen={s.pen} active={isPen} />
      </button>

      <button aria-pressed={isHl} title="Marca-texto"
        onClick={() => pick('draw', 'highlighter', { active: 'highlighter' })}>
        <PenChip pen={s.highlighter} active={isHl} />
      </button>

      <button aria-pressed={s.mode === 'eraser'} title="Borracha" onClick={() => pick('eraser', 'eraser')}>⌫</button>
      <button aria-pressed={s.mode === 'select'} title="Seleção" onClick={() => pick('select', 'select')}>◌</button>
      <button title="Escrever com o teclado" onClick={p.onKeyboard}>⌨</button>

      <span className="tool-sep" />
      <button title="Desfazer" onClick={p.onUndo}>↶</button>
      <button title="Refazer" onClick={p.onRedo}>↷</button>
      <button title="Aproximar" onClick={() => p.onZoom(1.25)}>＋</button>
      <button title="Afastar" onClick={() => p.onZoom(0.8)}>－</button>
      <button title="Ajustar à largura" onClick={p.onFit}>⤢</button>

      <span className="spacer" />
      <button onClick={p.onMenu} title="Menu da página">⋯</button>
      <button className="primary" onClick={p.onSend} disabled={!p.sendCount || p.sending}>
        {p.sending ? 'Lendo…' : `Enviar (${p.sendCount})`}
      </button>

      {open === 'pen' ? (
        <div className="popover">
          <div className="pop-title">Ponta</div>
          <div className="chip-row">
            {PEN_ORDER.map((kind: PenKind) => (
              <button
                key={kind}
                className="chip"
                aria-pressed={s.pen.kind === kind}
                onClick={() => set({
                  pen: {
                    ...s.pen, kind,
                    size: PEN_PROFILES[kind].defaultSize,
                    opacity: PEN_PROFILES[kind].defaultOpacity,
                  },
                })}
              >
                {PEN_PROFILES[kind].label}
              </button>
            ))}
          </div>

          <div className="pop-title">Espessura</div>
          <SizeRow
            sizes={profileOf(s.pen.kind).sizes} value={s.pen.size} color={s.pen.color}
            onPick={(size) => set({ pen: { ...s.pen, size } })}
          />

          {profileOf(s.pen.kind).opacityAdjustable ? (
            <>
              <div className="pop-title">Opacidade — {Math.round(s.pen.opacity * 100)}%</div>
              <input
                type="range" min={10} max={100} value={Math.round(s.pen.opacity * 100)}
                onChange={(e) => set({ pen: { ...s.pen, opacity: Number(e.target.value) / 100 } })}
              />
            </>
          ) : null}

          <div className="pop-title">Cor</div>
          <ColorGrid
            colors={INK_COLORS} value={s.pen.color}
            onPick={(color) => { setRecent(pushRecent(color)); set({ pen: { ...s.pen, color } }); }}
          />
          {recent.length ? (
            <>
              <div className="pop-title">Recentes</div>
              <ColorGrid colors={recent} value={s.pen.color} onPick={(color) => set({ pen: { ...s.pen, color } })} />
            </>
          ) : null}
          <label className="custom-color">
            <span>Outra cor</span>
            <input
              type="color" value={s.pen.color}
              onChange={(e) => { setRecent(pushRecent(e.target.value)); set({ pen: { ...s.pen, color: e.target.value } }); }}
            />
          </label>
        </div>
      ) : null}

      {open === 'highlighter' ? (
        <div className="popover">
          <div className="pop-title">Cor do marca-texto</div>
          <ColorGrid
            colors={HIGHLIGHTER_COLORS} value={s.highlighter.color}
            onPick={(color) => set({ highlighter: { ...s.highlighter, color } })}
          />
          <div className="pop-title">Espessura</div>
          <SizeRow
            sizes={PEN_PROFILES.highlighter.sizes} value={s.highlighter.size} color={s.highlighter.color}
            onPick={(size) => set({ highlighter: { ...s.highlighter, size } })}
          />
          <div className="pop-title">Opacidade — {Math.round(s.highlighter.opacity * 100)}%</div>
          <input
            type="range" min={10} max={80} value={Math.round(s.highlighter.opacity * 100)}
            onChange={(e) => set({ highlighter: { ...s.highlighter, opacity: Number(e.target.value) / 100 } })}
          />
          <p className="pop-hint">O marca-texto fica por baixo da tinta e não conta como conteúdo para a leitura.</p>
        </div>
      ) : null}

      {open === 'eraser' ? (
        <div className="popover">
          <div className="pop-title">Borracha</div>
          <div className="chip-row">
            <button
              className="chip" aria-pressed={s.eraser.mode === 'stroke'}
              onClick={() => set({ eraser: { ...s.eraser, mode: 'stroke' } })}
            >
              Por traço
            </button>
            <button
              className="chip" aria-pressed={s.eraser.mode === 'area'}
              onClick={() => set({ eraser: { ...s.eraser, mode: 'area' } })}
            >
              Por área
            </button>
          </div>
          <p className="pop-hint">
            {s.eraser.mode === 'stroke'
              ? 'Apaga a linha inteira que a borracha tocar.'
              : 'Apaga só o pedaço por onde a borracha passar.'}
          </p>

          <div className="pop-title">Tamanho — {s.eraser.size}</div>
          <input
            type="range" min={8} max={80} value={s.eraser.size}
            onChange={(e) => set({ eraser: { ...s.eraser, size: Number(e.target.value) } })}
          />

          <label className="switch" style={{ marginTop: 4 }}>
            <input
              type="checkbox" checked={s.eraser.highlighterOnly}
              onChange={(e) => set({ eraser: { ...s.eraser, highlighterOnly: e.target.checked } })}
            />
            <span className="label">Apagar só marca-texto</span>
          </label>

          {confirmErase ? (
            <div className="field" style={{ marginTop: 8 }}>
              <span className="hint">Isto apaga toda a tinta da página. Dá para desfazer com ↶.</span>
              <div className="row wrap">
                <button onClick={() => setConfirmErase(false)}>Cancelar</button>
                <span className="spacer" />
                <button
                  className="danger"
                  onClick={() => { p.onEraseAll(); setConfirmErase(false); setOpen(null); }}
                >
                  Apagar tudo mesmo
                </button>
              </div>
            </div>
          ) : (
            /* Um toque só aqui já apagou uma página inteira de verdade.
               Agora pede confirmação. */
            <button className="danger" style={{ marginTop: 8 }} onClick={() => setConfirmErase(true)}>
              Apagar tudo desta página
            </button>
          )}
        </div>
      ) : null}

      {open === 'select' ? (
        <div className="popover">
          <div className="pop-title">Seleção</div>
          <div className="chip-row">
            <button className="chip" aria-pressed={s.select === 'lasso'} onClick={() => set({ select: 'lasso' })}>Laço</button>
            <button className="chip" aria-pressed={s.select === 'rect'} onClick={() => set({ select: 'rect' })}>Retângulo</button>
          </div>
          <p className="pop-hint">Circule ou enquadre os traços. Depois dá para arrastar, trocar a cor ou apagar.</p>
        </div>
      ) : null}

      {open === 'selection' && p.selectionCount ? (
        <div className="popover">
          <div className="pop-title">{p.selectionCount} traço(s) selecionado(s)</div>
          <p className="pop-hint">Arraste na página para mover.</p>
          <div className="pop-title">Trocar a cor</div>
          <ColorGrid colors={INK_COLORS} value="" onPick={p.onRecolorSelection} />
          <button className="danger" onClick={p.onDeleteSelection}>Apagar selecionados</button>
        </div>
      ) : null}
    </div>
  );
}

/** A ponta que o motor deve usar agora. */
export function activePen(state: ToolState): PenSettings {
  return state.active === 'highlighter' ? state.highlighter : state.pen;
}
